import type { FC, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthStatus } from '@/features/user'
import {
  STORAGE_KEY_PREFIX,
  getActiveKey,
  rotateKey as rotateKeyRing,
  saveKeyRing,
  loadKeyRing as loadKeyRingFromStorage,
  clearKeyRing as clearKeyRingFromStorage,
  exportKeyRingToFile,
  importKeyRingFromFile,
  isValidKeyRingShape,
} from '@/infra/crypto/key-ring'
import type { KeyRing, StorageMode } from '@/infra/crypto/key-ring'
import { encryptField, decryptField, type EncryptedPayload } from '@/infra/crypto/payloads'
import { CryptoContext, type CryptoContextValue } from './CryptoContext'

interface CryptoProviderProps {
  readonly children: ReactNode
}

// eslint-disable-next-line react-refresh/only-export-components
export const CryptoProvider: FC<CryptoProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuthStatus()
  const userEmail = user?.profile?.email ?? null

  const [keyRing, setKeyRing] = useState<KeyRing | null>(null)
  const [storageMode, setStorageMode] = useState<StorageMode>('memory')
  const [cryptoInitialized, setCryptoInitialized] = useState(false)

  const userEmailRef = useRef(userEmail)
  userEmailRef.current = userEmail

  const keyRingRef = useRef(keyRing)
  keyRingRef.current = keyRing

  const storageModeRef = useRef(storageMode)
  storageModeRef.current = storageMode

  useEffect(() => {
    if (!userEmail) {
      setCryptoInitialized(true)
      return
    }

    const existing = loadKeyRingFromStorage(userEmail, 'localStorage')
    if (existing) {
      setKeyRing(existing)
      setStorageMode('localStorage')
    }
    setCryptoInitialized(true)
  }, [userEmail])

  useEffect(() => {
    const handleStorageEvent = (event: StorageEvent) => {
      if (!userEmailRef.current) return

      const expectedKey = `${STORAGE_KEY_PREFIX}${userEmailRef.current}`
      if (event.key !== expectedKey) return

      if (event.newValue === null) {
        setKeyRing(null)
        return
      }

      try {
        const parsed: unknown = JSON.parse(event.newValue)
        if (isValidKeyRingShape(parsed)) {
          setKeyRing(parsed)
        }
      } catch {
        /* ignored */
      }
    }

    globalThis.addEventListener('storage', handleStorageEvent)
    return () => globalThis.removeEventListener('storage', handleStorageEvent)
  }, [])

  const previousAuthRef = useRef(isAuthenticated)
  useEffect(() => {
    if (previousAuthRef.current && !isAuthenticated) {
      if (userEmailRef.current) {
        clearKeyRingFromStorage(userEmailRef.current)
      }
      setKeyRing(null)
    }
    previousAuthRef.current = isAuthenticated
  }, [isAuthenticated])

  const loadKeyRingHandler = useCallback((incoming: KeyRing) => {
    setKeyRing(incoming)
    saveKeyRing(incoming, storageModeRef.current)
  }, [])

  const clearKeys = useCallback(() => {
    if (userEmailRef.current) {
      clearKeyRingFromStorage(userEmailRef.current)
    }
    setKeyRing(null)
  }, [])

  const rotateKeyHandler = useCallback(async (): Promise<KeyRing> => {
    const currentKeyRing = keyRingRef.current
    if (!currentKeyRing) {
      throw new Error(
        'Cannot rotate key: no key ring loaded. Load a key ring via loadKeyRing() or generate one first.',
      )
    }

    const updated = await rotateKeyRing(currentKeyRing)
    setKeyRing(updated)
    saveKeyRing(updated, storageModeRef.current)
    return updated
  }, [])

  const setStorageModeHandler = useCallback((mode: StorageMode) => {
    setStorageMode(mode)

    const currentKeyRing = keyRingRef.current
    if (currentKeyRing) {
      saveKeyRing(currentKeyRing, mode)
    }
  }, [])

  const exportKeyRingHandler = useCallback(() => {
    const currentKeyRing = keyRingRef.current
    if (!currentKeyRing) {
      throw new Error(
        'Cannot export: no key ring loaded. Load a key ring via loadKeyRing() or generate one first.',
      )
    }
    exportKeyRingToFile(currentKeyRing)
  }, [])

  const importKeyRingHandler = useCallback(async (file: File): Promise<void> => {
    const imported = await importKeyRingFromFile(file)
    setKeyRing(imported)
    saveKeyRing(imported, storageModeRef.current)
  }, [])

  const encrypt = useCallback(
    async (data: string | Record<string, unknown>): Promise<EncryptedPayload> => {
      const currentKeyRing = keyRingRef.current
      if (!currentKeyRing) {
        throw new Error(
          'Cannot encrypt: no key ring loaded. Load a key ring via loadKeyRing() or generate one first.',
        )
      }

      const active = getActiveKey(currentKeyRing)
      if (!active) {
        throw new Error(
          'Cannot encrypt: key ring has no keys. Rotate the key ring to generate a new key.',
        )
      }

      return encryptField(data, active.key, active.version)
    },
    [],
  )

  const decrypt = useCallback(async (payload: EncryptedPayload): Promise<unknown> => {
    const currentKeyRing = keyRingRef.current
    if (!currentKeyRing) {
      throw new Error(
        'Cannot decrypt: no key ring loaded. Load a key ring via loadKeyRing() or import one first.',
      )
    }

    return decryptField(payload, currentKeyRing)
  }, [])

  const activeKey = keyRing ? getActiveKey(keyRing) : null

  const contextValue = useMemo<CryptoContextValue>(
    () => ({
      keyRingStatus: keyRing ? 'loaded' : 'missing',
      cryptoInitialized,
      storageMode,
      isReady: keyRing !== null,
      activeKeyVersion: activeKey?.version ?? null,
      keys: keyRing?.keys ?? [],
      loadKeyRing: loadKeyRingHandler,
      clearKeys,
      rotateKey: rotateKeyHandler,
      setStorageMode: setStorageModeHandler,
      exportKeyRing: exportKeyRingHandler,
      importKeyRing: importKeyRingHandler,
      encrypt,
      decrypt,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      keyRing,
      cryptoInitialized,
      storageMode,
      loadKeyRingHandler,
      clearKeys,
      rotateKeyHandler,
      setStorageModeHandler,
      exportKeyRingHandler,
      importKeyRingHandler,
      encrypt,
      decrypt,
    ],
  )

  return <CryptoContext.Provider value={contextValue}>{children}</CryptoContext.Provider>
}
